import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { selectOptionsCache } from "../utils/selectOptionsCache";

import FilterRequest = App.Http.Requests.FilterRequest;
import PageRequest = App.Http.Requests.PageRequest;

interface SelectOption {
    label: string;
    value: string | number;
}

interface QueryParams {
    filter?: FilterRequest;
    page?: PageRequest;
}

interface UseSelectOptionsParams<TData> {
    apiFunction: (payload: QueryParams) => Promise<unknown>;
    dataKey: string;
    cacheKey: string;
    queryParams?: QueryParams;
    mapToOption: (item: TData) => SelectOption;
    enabled?: boolean;
}

type CacheStatus = "checking" | "hit" | "waiting" | "fetching";

export const useSelectOptions = <TData>({
    apiFunction,
    dataKey,
    cacheKey,
    queryParams = {},
    mapToOption,
    enabled = true,
}: UseSelectOptionsParams<TData>) => {
    // Memoize queryParams to prevent infinite loops from new object references
    const queryParamsKey = JSON.stringify(queryParams);
    const stableQueryParams = useMemo(() => queryParams, [queryParamsKey]);

    // Stabilize apiFunction to prevent infinite loops from inline arrow functions
    const apiFunctionRef = useRef(apiFunction);
    apiFunctionRef.current = apiFunction;
    const stableApiFunction = useCallback(
        (payload: QueryParams) => apiFunctionRef.current(payload),
        []
    );

    const fullCacheKey = useMemo(() =>
        selectOptionsCache.createCacheKey(cacheKey, stableQueryParams),
        [cacheKey, stableQueryParams]
    );

    const [cachedOptions, setCachedOptions] = useState<SelectOption[]>([]);
    const [cacheStatus, setCacheStatus] = useState<CacheStatus>("checking");
    const [refetchTrigger, setRefetchTrigger] = useState(0);

    // Track ref count for cleanup when all components unmount (page navigation)
    useEffect(() => {
        selectOptionsCache.addRef(fullCacheKey);
        return () => {
            selectOptionsCache.removeRef(fullCacheKey);
        };
    }, [fullCacheKey]);

    // Check cache and handle deduplication on mount
    useEffect(() => {
        if (!enabled) {
            setCacheStatus("checking");
            return;
        }

        // 1. Check if data is already cached
        const cached = selectOptionsCache.getCachedData<TData>(fullCacheKey);
        if (cached) {
            setCachedOptions(cached.options);
            setCacheStatus("hit");
            return;
        }

        // 2. Try to register atomically - first component wins
        const registration = selectOptionsCache.tryRegisterPendingRequest(fullCacheKey);
        if (registration === "already-pending") {
            setCacheStatus("waiting");
            // Subscribe to be notified when data is ready
            const unsubscribe = selectOptionsCache.subscribe(fullCacheKey, () => {
                const freshCached = selectOptionsCache.getCachedData<TData>(fullCacheKey);
                if (freshCached) {
                    setCachedOptions(freshCached.options);
                    setCacheStatus("hit");
                }
            });
            return unsubscribe;
        }

        // 3. We successfully registered - fetch
        setCacheStatus("fetching");
    }, [fullCacheKey, enabled, refetchTrigger]);

    // Only fetch if we're the designated fetcher
    const shouldFetch = enabled && cacheStatus === "fetching";
    const query = useFetchAllPaginatedData(
        stableApiFunction,
        stableQueryParams,
        dataKey as never,
        { enabled: shouldFetch }
    );

    // Track when fetch actually started (isLoading or isRefetching became true)
    const hasStartedLoadingRef = useRef(false);
    useEffect(() => {
        if (shouldFetch && (query.isLoading || query.isRefetching)) {
            hasStartedLoadingRef.current = true;
        }
    }, [shouldFetch, query.isLoading, query.isRefetching]);

    // Reset loading tracker when cache key changes
    useEffect(() => {
        hasStartedLoadingRef.current = false;
    }, [fullCacheKey]);

    // Map fresh data to options
    const freshOptions = useMemo(() => {
        if (!query.data?.length || cacheStatus !== "fetching") return [];
        return (query.data as TData[]).map(mapToOption);
    }, [query.data, mapToOption, cacheStatus]);

    // Cache fresh data when fetch completes successfully
    useEffect(() => {
        const isComplete = !query.isLoading && !query.isRefetching;
        if (cacheStatus === "fetching" && hasStartedLoadingRef.current && isComplete && !query.error) {
            selectOptionsCache.setCachedData(fullCacheKey, query.data as TData[], freshOptions);
            setCachedOptions(freshOptions);
            setCacheStatus("hit");
        }
    }, [query.data, query.isLoading, query.isRefetching, query.error, freshOptions, fullCacheKey, cacheStatus]);

    // Clean up pending request on error
    useEffect(() => {
        const isComplete = !query.isLoading && !query.isRefetching;
        if (cacheStatus === "fetching" && hasStartedLoadingRef.current && isComplete && query.error) {
            selectOptionsCache.cancelPendingRequest(fullCacheKey);
        }
    }, [query.isLoading, query.isRefetching, query.error, fullCacheKey, cacheStatus]);

    // Refetch function that invalidates cache and triggers re-fetch
    const refetch = useCallback(() => {
        selectOptionsCache.invalidate(fullCacheKey);
        hasStartedLoadingRef.current = false;
        setCacheStatus("checking");
        setRefetchTrigger(prev => prev + 1);
    }, [fullCacheKey]);

    const options = cacheStatus === "hit" ? cachedOptions : freshOptions;
    // Loading when: checking cache, waiting for another fetch, or actively fetching
    const isQueryActive = query.isLoading || query.isRefetching;
    const loading = cacheStatus === "checking" || cacheStatus === "waiting" || (cacheStatus === "fetching" && (isQueryActive || !hasStartedLoadingRef.current));
    const error = cacheStatus === "fetching" ? query.error : null;

    return {
        options,
        loading,
        error,
        refetch,
    };
};